"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"

import { QuickLoginPanel } from "@/components/dev/quick_login_panel"
import SalesforceLoginButton from "@/components/salesforce-login-button"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { Wordmark } from "@/components/ui/wordmark"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { toFieldError } from "@/lib/utils"
import { newIdentityPasswordResetPath, signInPath, signUpPath } from "@/routes"

// ----------------------------------------------------------------------------
// Form Pattern: Uncontrolled Inputs + FormData
// ----------------------------------------------------------------------------
// We follow the UUI (Untitled UI) demo pattern: uncontrolled inputs with
// native FormData collection. This makes copy-pasting UUI examples trivial.
//
// Why not Inertia's useForm?
// - useForm requires controlled inputs (value + onChange on every field)
// - React Aria's Input uses onChange(value: string), not onChange(event)
// - UUI demos never use controlled inputs - they use FormData
//
// What we get instead:
// - errors: via usePage().props.errors (inertia_rails: always_include_errors_hash)
// - processing: local useState (simple, explicit)
// - CSRF/redirects: handled by router.post() automatically
// ----------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type PageProps = {
  errors: Record<string, string>
}

export default function Login() {
  // Errors come from page props - Rails passes them via redirect_to(..., inertia: {errors: ...})
  // or they're included automatically when validation fails (always_include_errors_hash: true)
  const { errors } = usePage<PageProps>().props

  // Simple processing state - no need for useForm's machinery here
  const [processing, setProcessing] = useState(false)

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // FormData: the browser-native, UUI-idiomatic way to collect form values.
    // No useState per field. No onChange handlers. Just name attributes.
    router.post(
      signInPath(),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthSplitLayout>
      <Head title="Log in" />
      <div className="flex flex-col items-center justify-center gap-6">
        <Link href="/" variant="muted" className="hover:opacity-80">
          <Wordmark size={30} className="text-primary" />
        </Link>
        <div className="flex flex-col gap-2 text-center md:gap-3">
          <h1 className="text-display-xs md:text-display-sm text-primary">
            Welcome back
          </h1>
          <p className="text-md text-tertiary">
            Welcome back! Please enter your details.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          {/* Uncontrolled input: no value, no onChange. The `name` attribute is all FormData needs. */}
          <Input
            label="Email"
            type="email"
            name="email"
            placeholder="Enter your email"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.email}
            error={toFieldError(errors?.email)}
            autoFocus
            autoComplete="email"
          />
          <Input
            label="Password"
            type="password"
            name="password"
            size="md"
            placeholder="••••••••"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password}
            error={toFieldError(errors?.password)}
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center">
          <Link
            href={newIdentityPasswordResetPath()}
            className="text-brand-secondary hover:text-brand-secondary_hover ml-auto text-sm font-semibold"
          >
            Forgot password
          </Link>
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            size="lg"
            isLoading={processing}
            showTextWhileLoading
          >
            Sign in
          </Button>
        </div>
      </form>

      <div className="flex items-center gap-4">
        <div className="bg-secondary h-px flex-1" />
        <span className="text-tertiary text-sm">or</span>
        <div className="bg-secondary h-px flex-1" />
      </div>

      <SalesforceLoginButton />

      <div className="flex justify-center gap-1 text-center">
        <span className="text-tertiary text-sm">
          Don&apos;t have an account?
        </span>
        <Link
          href={signUpPath()}
          className="text-brand-secondary hover:text-brand-secondary_hover text-sm font-semibold"
        >
          Sign up
        </Link>
      </div>
      <QuickLoginPanel />
    </AuthSplitLayout>
  )
}
